/**
 * useReferralField hook
 * Owns the register page's referral/promo-code field: value, `?ref=` deep-link
 * prefill, debounced three-state validation, the lock-after-valid affordance,
 * and the promo-code US-005 signup gate.
 *
 * Extracted from RegisterPage (2026-07-16) when the page split into per-method
 * signup cards. The email and Google cards render the SAME binding returned by
 * ONE hook call — that shared binding is what keeps the two rendered fields in
 * sync, so a code typed in either card appears in the other and no one retypes
 * it after switching door. It also means the two fields can never disagree
 * about a code's validity, which matters now that validity gates both doors.
 *
 * IF THE FIELDS ARE EVER UN-SYNCED: call this hook twice (once per card). Do
 * NOT share a single createDebouncedReferralValidator() instance between two
 * fields — that factory (services/referral.ts) closes over ONE timer + waiters
 * array, so two fields sharing an instance would cross-cancel and resolve each
 * other's codes. Each hook call memoizes its own instance, so calling it twice
 * is safe by construction.
 *
 * TS-015 (T2/T4) behaviour preserved verbatim:
 *   - `?ref=CODE` one-shot prefill on mount, editable (TO-LL-013 useSearchParams).
 *   - Debounced soft validation reusing TS-012's createDebouncedReferralValidator.
 *   - BUG-20260702-205850 lock-after-valid (Option A).
 *
 * promo-code US-005 (T3) / FR-2.8 / AC-005.6: BR-TS015.1 ("a code never blocks
 * signup") is DELIBERATELY SUPERSEDED for the case a code settles as genuinely
 * INVALID — `isInvalid` disables BOTH doors. It is NOT superseded for the
 * fail-open 'undecidable' outage window (AC-005.3), which still only produces
 * the gentle advisory notice. Read the ValidationOutcome docs in
 * @/types/referral before touching the effect below: reporting a transport
 * failure as 'invalid' would fail the gate CLOSED and lock parents out of
 * signup during an outage.
 */

import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createDebouncedReferralValidator } from '@/services/referral';

/**
 * TS-015 / T4: gentle, non-blocking notice copy for an unrecognised referral
 * code (BR-TS015.1). Still used for the fail-open advisory path.
 */
export const REFERRAL_NOT_RECOGNISED_MSG =
  "That referral code isn't recognised. You can still sign up — but the friend who referred you won't be credited.";

/**
 * promo-code US-005 (T3) / AC-005.1 / AC-005.5: dash-free blocking error shown
 * only when the code settles as invalid for the CURRENT field value. This is
 * the ONE state that disables BOTH the native Sign Up button and the Continue
 * with Google button (FR-2.3/FR-2.7).
 */
export const CODE_INVALID_MSG =
  'That code is not valid. Please check it, or clear the field to continue.';

export interface ReferralFieldBinding {
  /** Raw input value, exactly as typed. */
  code: string;
  /** Trimmed value for the submit payload / Google button prop. */
  trimmedCode: string;
  /** Advisory notice — never blocks. Suppressed while `isInvalid` is true. */
  notice: string | null;
  /** True once a URL-prefilled code has validated and is still untouched. */
  isLocked: boolean;
  /**
   * promo-code US-005 gate: true ONLY when the current non-empty code settled
   * as genuinely invalid. Drives BOTH doors. 'idle'/'valid'/'undecidable' all
   * leave this false (fail open, AC-005.2/AC-005.3).
   */
  isInvalid: boolean;
  /** Records the edit (forfeiting the lock) and updates the value. */
  onChange: (value: string) => void;
}

export function useReferralField(): ReferralFieldBinding {
  // TS-015 / T2: read the `?ref=` deep-link query param (TO-LL-013 —
  // useSearchParams, never manual string parsing).
  const [searchParams] = useSearchParams();

  const [code, setCode] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  // BUG-20260702-205850: three pieces of state drive the lock —
  //   - fromUrl: the code was prefilled from the URL (not typed).
  //   - touched: the parent edited the field at least once, so a typo'd link
  //     stays correctable (any manual edit permanently forfeits the lock).
  //   - valid: the debounced validation confirmed the code is recognised.
  const [fromUrl, setFromUrl] = useState(false);
  const [touched, setTouched] = useState(false);
  const [valid, setValid] = useState(false);
  // promo-code US-005 (T3) / AC-005.1-AC-005.4: the settled three-state gate
  // outcome for the CURRENT code value. 'idle' = empty field (fail open,
  // AC-005.2). Only written inside the `active` stale-guard below, so an
  // in-flight/debouncing value never disables the buttons (AC-005.4) and a
  // superseded keystroke's late result is dropped.
  const [gateState, setGateState] = useState<'idle' | 'valid' | 'invalid' | 'undecidable'>('idle');

  // TS-015 / T2: one-shot prefill from `?ref=CODE` on mount. Empty deps so it
  // never re-syncs on later renders — the field stays editable and user edits
  // are never clobbered. Absent/empty `?ref` => no prefill, no notice.
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref && ref.trim()) {
      setCode(ref.trim());
      // Remember it came from the URL so it can be locked once validated.
      // A later manual edit flips `touched` and forfeits the lock regardless.
      setFromUrl(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TS-015 / T4: ONE stable debounced validator instance for this field's
  // lifetime. Memoized per hook call — see the header note on why two fields
  // must never share one instance.
  const debouncedValidate = useMemo(() => createDebouncedReferralValidator(), []);

  // TS-015 / T4 + promo-code US-004 (T5) / US-005 (T3): debounced validation.
  // The `active` guard ignores stale results from superseded keystrokes.
  useEffect(() => {
    const trimmed = code.trim();
    if (!trimmed) {
      setNotice(null);
      setValid(false);
      // promo-code US-005 (T3) / AC-005.2/AC-005.3: empty field -> 'idle' ->
      // fail open (buttons enabled). KEEP this early return so the unmocked
      // central RegisterPage test packs never trigger a network call
      // (test-isolation guard, per the T3 implementation guidance).
      setGateState('idle');
      return;
    }
    let active = true;
    // promo-code US-004 (T5): debouncedValidate resolves a three-state
    // ReferralValidationResult ({outcome, data?}) — NOT {valid}. An
    // 'undecidable' outcome (transport/timeout/5xx — AC-004.4) is deliberately
    // NOT treated as invalid: it leaves the notice/lock state unchanged rather
    // than wrongly flashing "not recognised" during a transient outage.
    debouncedValidate(trimmed).then((result) => {
      if (!active) return;
      // promo-code US-005 (T3) / AC-005.1/AC-005.3: store the settled
      // three-state outcome (including 'undecidable') for the gate. Enforces
      // TO-LL-036 — an outage must fail OPEN, never be mistaken for a
      // confirmed 'invalid' decision.
      setGateState(result.outcome);
      if (result.outcome !== 'undecidable') {
        const isValid = result.outcome === 'valid';
        setNotice(isValid ? null : REFERRAL_NOT_RECOGNISED_MSG);
        // Tracked only so a URL-prefilled, untouched code can lock once
        // confirmed.
        setValid(isValid);
      }
    });
    return () => {
      active = false;
    };
  }, [code, debouncedValidate]);

  // promo-code US-005 (T3) / AC-005.1-AC-005.4: true only when the CURRENT,
  // non-empty code has settled as invalid. Deliberately supersedes BR-TS015.1
  // for this case only (FR-2.8). NOTE: the frontend never reads promo_config
  // directly — when the promo kill switch is OFF, US-004/US-006 guarantee the
  // wrapper never yields 'invalid', so this gate reverts to never-block
  // automatically (BR-005.1). Do not add a redundant frontend flag.
  const isInvalid = gateState === 'invalid';

  // BUG-20260702-205850: lock (read-only) only when the code came from the
  // `?ref=` link, has not been manually edited, AND validated valid. Editable
  // during the validating window and whenever the code is unrecognised, so a
  // bad link is always correctable.
  const isLocked = fromUrl && !touched && valid;

  return {
    code,
    trimmedCode: code.trim(),
    // The blocking error and the advisory notice are mutually exclusive: when
    // the gate blocks, ReferralCodeField renders CODE_INVALID_MSG instead, so
    // the user never sees two conflicting messages for one outcome.
    notice,
    isLocked,
    isInvalid,
    onChange: (value: string) => {
      // Any manual edit forfeits the lock, so a typo'd link value can still be
      // corrected before it validates.
      setTouched(true);
      setCode(value);
    },
  };
}
